import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import Card from '../ui/Card';
import { formatDate } from '../../utils/formatters';

export default function SectionPlanning({ planning }) {
  const { duration, milestones, phases } = planning;

  return (
    <section id="planning" className="section-padding bg-secondary-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Planning du Projet</h2>
          <p className="text-xl text-secondary-200 max-w-2xl mx-auto">
            {duration.note}
          </p>
        </motion.div>

        {/* Timeline visuelle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto mb-12"
        >
          <Card padding="lg">
            <div className="space-y-6">
              {milestones.map((milestone, index) => {
                const Icon = getIconForType(milestone.type);

                return (
                  <div
                    key={milestone.id}
                    className="flex items-start gap-4 pb-6 border-b border-secondary-200 last:border-b-0"
                  >
                    {/* Icône & date */}
                    <div className="flex-shrink-0 text-center">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                          milestone.critical
                            ? 'bg-red-900/30 text-red-400 border-2 border-red-500'
                            : milestone.type === 'payment'
                            ? 'bg-green-900/30 text-green-400 border-2 border-green-500'
                            : milestone.type === 'meeting'
                            ? 'bg-primary-900/30 text-primary-400 border-2 border-primary-500'
                            : 'bg-secondary-700 text-secondary-200 border-2 border-secondary-500'
                        }`}
                      >
                        <Icon size={24} />
                      </div>
                      <div className="text-xs text-secondary-300 font-medium">
                        {formatDate(milestone.date, 'short')}
                      </div>
                    </div>

                    {/* Contenu */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h4 className="font-bold text-lg text-white">{milestone.label}</h4>
                        {milestone.critical && (
                          <span className="px-2 py-1 bg-red-900/30 border border-red-500 text-red-400 text-xs font-semibold rounded-full whitespace-nowrap">
                            🔴 Critique
                          </span>
                        )}
                      </div>

                      {/* Type de milestone */}
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            milestone.type === 'payment'
                              ? 'bg-green-900/30 text-green-400 border border-green-700'
                              : milestone.type === 'meeting'
                              ? 'bg-primary-900/30 text-primary-400 border border-primary-700'
                              : milestone.type === 'deliverable'
                              ? 'bg-purple-900/30 text-purple-400 border border-purple-700'
                              : milestone.type === 'validation'
                              ? 'bg-blue-900/30 text-blue-400 border border-blue-700'
                              : 'bg-secondary-700 text-secondary-200 border border-secondary-600'
                          }`}
                        >
                          {milestone.type === 'payment' && '💰 Paiement'}
                          {milestone.type === 'meeting' && '🤝 Réunion'}
                          {milestone.type === 'deliverable' && '📦 Livrable'}
                          {milestone.type === 'validation' && '✅ Validation'}
                          {milestone.type === 'milestone' && '🎯 Jalon'}
                        </span>
                      </div>

                      {/* Note */}
                      {milestone.note && (
                        <p className="text-sm text-secondary-200 bg-secondary-800 rounded p-3 mt-2">
                          {milestone.note}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </motion.div>

        {/* Phases du projet */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h3 className="text-2xl font-bold mb-6 text-center text-white">Les 4 Phases du Projet</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {phases.map((phase, index) => (
              <Card key={phase.id} hover>
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex items-center justify-center w-10 h-10 bg-primary-500 text-white rounded-full font-bold">
                    {index + 1}
                  </span>
                  <h4 className="font-bold text-lg text-white">{phase.name}</h4>
                </div>
                <p className="text-sm text-secondary-200 mb-4">
                  {formatDate(phase.start, 'short')} → {formatDate(phase.end, 'short')}
                </p>
                <ul className="space-y-2">
                  {phase.tasks.map((task, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Icons.CheckCircle2 size={16} className="text-primary-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-secondary-200">{task.name}</span>
                        {task.duration && (
                          <span className="text-secondary-400"> ({task.duration})</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Helper pour obtenir l'icône selon le type
function getIconForType(type) {
  switch (type) {
    case 'payment':
      return Icons.Euro;
    case 'meeting':
      return Icons.Users;
    case 'deliverable':
      return Icons.Package;
    case 'validation':
      return Icons.CheckCircle;
    case 'milestone':
      return Icons.Flag;
    default:
      return Icons.Circle;
  }
}
